import React, { createContext, useContext, useState, useEffect } from 'react';

// Email Provider Types - No API keys stored in frontend!
export interface EmailProvider {
  id: string;
  name: string;
  type: 'brevo' | 'mailgun' | 'auto';
  isActive: boolean;
}

export interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  htmlContent: string;
  textContent?: string;
  variables: string[];
}

export interface EmailRequest {
  to: string;
  toName?: string;
  subject: string;
  content: string;
  htmlContent?: string;
  templateId?: string;
  variables?: Record<string, string>;
  priority?: 'low' | 'normal' | 'high';
  tags?: string[];
}

export interface EmailHubContextType {
  // Provider Management
  providers: EmailProvider[];
  activeProvider: EmailProvider | null;
  setActiveProvider: (provider: EmailProvider) => void;
  
  // Email Sending (via Edge Function)
  sendEmail: (request: EmailRequest) => Promise<{ success: boolean; messageId?: string; provider?: string; error?: string }>;
  
  // Templates
  templates: EmailTemplate[];
  loadTemplates: () => Promise<void>;
  
  // Status
  isInitialized: boolean;
  lastError: string | null;
}

const EmailHubContext = createContext<EmailHubContextType | undefined>(undefined);

export const EmailHubProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [providers, setProviders] = useState<EmailProvider[]>([]);
  const [activeProvider, setActiveProvider] = useState<EmailProvider | null>(null);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);

  useEffect(() => {
    initializeEmailHub();
  }, []);

  const initializeEmailHub = async () => {
    try {
      // Define available providers (no API keys needed in frontend!)
      const emailProviders: EmailProvider[] = [
        {
          id: 'auto',
          name: 'Auto (Brevo → Mailgun)',
          type: 'auto',
          isActive: true
        },
        {
          id: 'brevo',
          name: 'Brevo (Sendinblue)',
          type: 'brevo',
          isActive: false
        },
        {
          id: 'mailgun',
          name: 'Mailgun',
          type: 'mailgun',
          isActive: false
        }
      ];

      setProviders(emailProviders);
      
      // Set auto provider as default
      const defaultProvider = emailProviders.find(p => p.isActive) || emailProviders[0];
      setActiveProvider(defaultProvider);

      await loadTemplates();
      setIsInitialized(true);
    } catch (error) {
      console.error('Failed to initialize EmailHub:', error);
      setLastError('Failed to initialize email providers');
    }
  };

  /**
   * Send email via Supabase Edge Function
   * API keys are stored securely server-side, not in the frontend!
   */
  const sendEmail = async (request: EmailRequest): Promise<{ success: boolean; messageId?: string; provider?: string; error?: string }> => {
    if (!activeProvider) {
      return { success: false, error: 'No active email provider configured' };
    }

    try {
      setLastError(null);

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !supabaseAnonKey) {
        throw new Error('Supabase configuration missing');
      }

      // Call the Edge Function - API keys are stored in Supabase secrets!
      const response = await fetch(`${supabaseUrl}/functions/v1/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify({
          to: request.to,
          toName: request.toName,
          subject: request.subject,
          content: request.content,
          htmlContent: request.htmlContent,
          templateId: request.templateId,
          variables: request.variables,
          provider: activeProvider.type, // 'auto', 'brevo', or 'mailgun'
          priority: request.priority,
          tags: request.tags
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send email');
      }

      return {
        success: result.success,
        messageId: result.messageId,
        provider: result.provider,
        error: result.error
      };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setLastError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const loadTemplates = async () => {
    // TODO: Load templates from Supabase or configuration
    const defaultTemplates: EmailTemplate[] = [
      {
        id: 'support-reply',
        name: 'Réponse Support',
        subject: 'Re: {{ticketSubject}}',
        htmlContent: `
          <div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
            <h2>Bonjour {{userName}},</h2>
            <p>Merci pour votre message concernant le ticket <strong>{{ticketNumber}}</strong>.</p>
            <div style="background-color: #f5f5f5; padding: 15px; border-radius: 5px; margin: 20px 0;">
              {{replyContent}}
            </div>
            <p>Si vous avez d'autres questions, n'hésitez pas à nous contacter.</p>
            <p>Cordialement,<br>L'équipe Support Komby</p>
          </div>
        `,
        variables: ['userName', 'ticketNumber', 'ticketSubject', 'replyContent']
      },
      {
        id: 'ticket-confirmation',
        name: 'Confirmation Ticket',
        subject: 'Votre demande de support a été reçue - Ticket {{ticketNumber}}',
        htmlContent: `
          <div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
            <h2>Bonjour {{userName}},</h2>
            <p>Nous avons bien reçu votre demande de support.</p>
            <p><strong>Numéro de ticket:</strong> {{ticketNumber}}</p>
            <p><strong>Sujet:</strong> {{ticketSubject}}</p>
            <p>Notre équipe vous répondra dans les plus brefs délais.</p>
            <p>Cordialement,<br>L'équipe Support Komby</p>
          </div>
        `,
        variables: ['userName', 'ticketNumber', 'ticketSubject']
      },
      {
        id: 'ticket-closure',
        name: 'Clôture Ticket',
        subject: '✅ Ticket {{ticketNumber}} fermé - {{ticketSubject}}',
        htmlContent: `
          <div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto;">
            <!-- Header -->
            <div style="background: #000000; padding: 30px; text-align: center; border-radius: 8px 8px 0 0;">
              <img src="https://komby.io/press/assets/logos/komby-wordmark-yellow-on-black.png" alt="Komby" width="220" style="display:block;margin:0 auto 12px auto;border:0;width:220px;height:auto;max-width:100%;" />
              <h1 style="color: #FFD300; margin: 0; font-size: 24px;">Ticket Fermé</h1>
              <p style="color: rgba(255,255,255,0.9); margin: 10px 0 0 0;">Komby Support</p>
            </div>

            <!-- Content -->
            <div style="background: white; padding: 30px; border: 1px solid #e5e7eb; border-top: none;">
              <h2 style="color: #1f2937; margin: 0 0 20px 0;">Bonjour {{userName}},</h2>
              
              <p style="margin-bottom: 25px;">
                Nous avons le plaisir de vous informer que votre demande de support a été traitée et le ticket correspondant a été fermé.
              </p>

              <!-- Ticket Details Box -->
              <div style="background-color: #f8fafc; border: 2px solid #e2e8f0; border-radius: 8px; padding: 25px; margin: 25px 0;">
                <h3 style="color: #1e40af; margin: 0 0 15px 0; border-bottom: 2px solid #dbeafe; padding-bottom: 10px;">
                  📋 Détails du ticket
                </h3>
                <table style="width: 100%; border-collapse: collapse;">
                  <tr>
                    <td style="padding: 8px 0; font-weight: bold; color: #374151; width: 40%;">Numéro de ticket :</td>
                    <td style="padding: 8px 0; color: #1f2937;">{{ticketNumber}}</td>
                  </tr>
                  <tr>
                    <td style="padding: 8px 0; font-weight: bold; color: #374151;">Sujet :</td>
                    <td style="padding: 8px 0; color: #1f2937;">{{ticketSubject}}</td>
                  </tr>
                  <tr>
                    <td style="padding: 8px 0; font-weight: bold; color: #374151;">Votre email :</td>
                    <td style="padding: 8px 0; color: #1f2937;">{{userEmail}}</td>
                  </tr>
                  <tr>
                    <td style="padding: 8px 0; font-weight: bold; color: #374151;">Problème rencontré :</td>
                    <td style="padding: 8px 0; color: #1f2937;">{{problemDescription}}</td>
                  </tr>
                  <tr>
                    <td style="padding: 8px 0; font-weight: bold; color: #374151;">Date de fermeture :</td>
                    <td style="padding: 8px 0; color: #10b981; font-weight: bold;">{{closureDate}}</td>
                  </tr>
                </table>
              </div>

              <!-- Success Message -->
              <div style="background: linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%); border: 2px solid #22c55e; border-radius: 8px; padding: 20px; margin: 25px 0; text-align: center;">
                <h3 style="color: #15803d; margin: 0 0 10px 0;">🎉 Résolution réussie !</h3>
                <p style="color: #166534; margin: 0;">Nous espérons que votre problème a été résolu de manière satisfaisante.</p>
              </div>

              <!-- Next Steps -->
              <div style="background-color: #fef3c7; border: 1px solid #f59e0b; border-radius: 8px; padding: 20px; margin: 25px 0;">
                <h4 style="color: #92400e; margin: 0 0 10px 0;">💡 Besoin d'aide supplémentaire ?</h4>
                <p style="color: #78350f; margin: 0;">
                  Si vous avez d'autres questions ou si le problème persiste, n'hésitez pas à créer un nouveau ticket de support via notre centre d'aide.
                </p>
              </div>

              <p style="margin: 30px 0 20px 0;">
                Merci de votre confiance et de nous avoir choisis pour résoudre votre problème.
              </p>

              <!-- Footer -->
              <div style="border-top: 2px solid #e5e7eb; padding-top: 20px; text-align: center; margin-top: 30px;">
                <p style="color: #6b7280; margin: 0 0 10px 0; font-weight: bold;">
                  Cordialement,<br>
                  L'équipe Support Komby
                </p>
                <p style="color: #9ca3af; font-size: 14px; margin: 0;">
                  🌟 Votre satisfaction est notre priorité | Support 24/7 disponible
                </p>
              </div>
            </div>
          </div>
        `,
        variables: ['userName', 'ticketNumber', 'ticketSubject', 'userEmail', 'problemDescription', 'closureDate']
      }
    ];

    setTemplates(defaultTemplates);
  };

  const value: EmailHubContextType = {
    providers,
    activeProvider,
    setActiveProvider,
    sendEmail,
    templates,
    loadTemplates,
    isInitialized,
    lastError
  };

  return (
    <EmailHubContext.Provider value={value}>
      {children}
    </EmailHubContext.Provider>
  );
};

export const useEmailHub = () => {
  const context = useContext(EmailHubContext);
  if (context === undefined) {
    throw new Error('useEmailHub must be used within an EmailHubProvider');
  }
  return context;
};
